/**
 * An organisation's reports (ADR-0043): mail traffic per day, counted as it
 * passes the spam filter, and storage per mailbox, as the mail store measures
 * it. Both download as CSV for a spreadsheet.
 */

import { useContext, useState } from 'react';
import type { MailboxList, TrafficDay } from 'zitimail-shared-types';

import { api } from './api';
import { AdminWhereContext, size } from './mailboxes';
import { Loaded, useLoad, when } from './ui';

const csvHref = (rows: ReadonlyArray<ReadonlyArray<string | number>>) =>
  `data:text/csv;charset=utf-8,${encodeURIComponent(
    rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\r\n'),
  )}`;

const KINDS = ['received', 'spam', 'rejected', 'sent'] as const;
const LABEL: Record<(typeof KINDS)[number], string> = {
  received: 'Received',
  spam: 'Spam (to Junk)',
  rejected: 'Refused',
  sent: 'Sent',
};

export function TrafficReport({ slug }: { slug: string }) {
  const { base } = useContext(AdminWhereContext);
  const [days, setDays] = useState(30);
  const [load] = useLoad(
    (signal) => api<{ days: TrafficDay[] }>(`${base(slug)}/traffic?days=${days}`, { signal }),
    `traffic:${slug}:${days}`,
  );
  return (
    <section className="adm-panel">
      <h3>Mail traffic</h3>
      <div className="adm-inline">
        <label>
          Period
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </label>
      </div>
      <Loaded load={load}>
        {({ days: rows }) => {
          const total = (k: (typeof KINDS)[number]) => rows.reduce((n, d) => n + d[k], 0);
          const newest = [...rows].reverse();
          return (
            <>
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Day (UTC)</th>
                    {KINDS.map((k) => (
                      <th key={k} className="adm-num">
                        {LABEL[k]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="adm-total">
                    <td>All {rows.length} days</td>
                    {KINDS.map((k) => (
                      <td key={k} className="adm-num">
                        {total(k).toLocaleString()}
                      </td>
                    ))}
                  </tr>
                  {newest.map((d) => (
                    <tr key={d.date}>
                      <td>{d.date}</td>
                      {KINDS.map((k) => (
                        <td key={k} className="adm-num">
                          {d[k].toLocaleString()}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p>
                <a
                  className="btn ghost"
                  download={`traffic-${slug}.csv`}
                  href={csvHref([
                    ['date', ...KINDS],
                    ...rows.map((d) => [d.date, ...KINDS.map((k) => d[k])]),
                  ])}
                >
                  Download CSV
                </a>
              </p>
            </>
          );
        }}
      </Loaded>
      <p className="adm-muted adm-small">
        Counted per message as mail passes the spam filter: received from outside, put in Junk,
        refused at the door (certain spam or a virus), and sent by your people. Mail between two of
        your people counts once, as sent.
      </p>
    </section>
  );
}

export function StorageReport({ slug }: { slug: string }) {
  const { base } = useContext(AdminWhereContext);
  const [load] = useLoad(
    (signal) => api<MailboxList>(`${base(slug)}/mailboxes`, { signal }),
    `mailboxes:${slug}`,
  );
  return (
    <section className="adm-panel">
      <h3>Storage</h3>
      <Loaded load={load}>
        {(list) => {
          const boxes = list.mailboxes
            .filter((m) => m.state !== 'deleted')
            .slice()
            .sort((a, b) => b.quota_used_bytes - a.quota_used_bytes);
          const pct = (used: number, of: number) =>
            of > 0 ? `${Math.round((used / of) * 100)}%` : '—';
          return (
            <>
              <p>
                {size(list.storage.used)} used by {boxes.length} mailboxes
                {list.storage.limit > 0
                  ? ` of ${size(list.storage.limit)} for the organisation (${pct(list.storage.used, list.storage.limit)})`
                  : ''}
                ; {size(list.storage.allocated)} allocated as quotas.
              </p>
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Mailbox</th>
                    <th className="adm-num">Used</th>
                    <th className="adm-num">Quota</th>
                    <th className="adm-num">Of quota</th>
                    <th>Measured</th>
                  </tr>
                </thead>
                <tbody>
                  {boxes.map((m) => (
                    <tr key={m.email}>
                      <td className="adm-mono">{m.email}</td>
                      <td className="adm-num">{size(m.quota_used_bytes)}</td>
                      <td className="adm-num">{size(m.quota_bytes)}</td>
                      <td className="adm-num">{pct(m.quota_used_bytes, m.quota_bytes)}</td>
                      <td>{m.quota_used_at ? when(m.quota_used_at) : 'not yet'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p>
                <a
                  className="btn ghost"
                  download={`storage-${slug}.csv`}
                  href={csvHref([
                    ['mailbox', 'used bytes', 'quota bytes', 'measured'],
                    ...boxes.map((m) => [
                      m.email,
                      m.quota_used_bytes,
                      m.quota_bytes,
                      m.quota_used_at ?? '',
                    ]),
                  ])}
                >
                  Download CSV
                </a>
              </p>
            </>
          );
        }}
      </Loaded>
    </section>
  );
}
